import { TDOId, TreeObjectId } from '@cbsa/state/modules/universal';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import {
  CreateNewFolderQueryResponse,
  RenameFolderQueryResponse,
} from '@redact/state/modules/casemedia/services/queries';
import { createAction } from '@reduxjs/toolkit';
import {
  DeleteCaseSDOBulkResponse,
  DeleteContentTemplateBulkResponse,
  FetchSoftDeletedFoldersResponse,
  FetchSoftDeletedTDOsResponse,
  RestoreSdosResponse,
  SetTrashFoldersPayload,
  SetTrashTDOsPayload,
} from '../services';
import type {
  CheckExistingTrashFolderQueryResponse,
  FetchSDOsBulkByTDOIdsResponse,
  FetchCaseChildTDOsResponse,
} from '../services';

export const actionFetchTrashTDOs = (payload: {
  limit: number;
  offset: number;
}) => FETCH_TRASH_TDOS(payload);
export const FETCH_TRASH_TDOS = createAction<{
  limit: number;
  offset: number;
}>('trash/fetch-trash-tdos');

export const FETCH_TRASH_TDOS_SUCCESS =
  createGraphQLSuccessAction<FetchSoftDeletedTDOsResponse>(
    'trash/fetch-trash-tdos-successfully'
  );
export const SET_TRASH_TDOS_DATA = createAction<SetTrashTDOsPayload>(
  'trash/set-trash-tdos-data'
);

export const FETCH_TRASH_TDOS_FAILURE = createGraphQLFailureAction<
  {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[]
>('trash/fetch-trash-tdos-failed');

export const UPDATE_SELECTED_FILE_ITEMS = createAction<{
  tdoIds: TDOId[];
}>('trash/update-selected-file-items');

export const actionUpdateSelectedFileItems = (payload: { tdoIds: TDOId[] }) =>
  UPDATE_SELECTED_FILE_ITEMS(payload);

// Drops both tables' selections and their row snapshots (e.g. on leaving Trash).
export const CLEAR_TRASH_SELECTION = createAction('trash/clear-selection');
export const actionClearTrashSelection = () => CLEAR_TRASH_SELECTION();

export const PERMANENT_DELETE_TDOS = createAction(
  'trash/permanent-delete-tdos'
);

export const PERMANENT_DELETE_FOLDERS = createAction(
  'trash/permanent-delete-folders'
);

export const RESTORE_TRASH_TDOS = createAction('trash/restore-trash-tdos');

export const RESTORE_TRASH_FOLDERS = createAction(
  'trash/restore-trash-folders'
);

export const actionPermanentDeleteTDOs = () => PERMANENT_DELETE_TDOS();

export const actionPermanentDeleteFolders = () => PERMANENT_DELETE_FOLDERS();

export const actionRestoreTDOs = () => RESTORE_TRASH_TDOS();

export const actionRestoreFolders = () => RESTORE_TRASH_FOLDERS();

export const UPDATE_TRASH_FILE_DATA_STORE = createAction<{
  tdoIds: TDOId[];
}>('trash/update-trash-file-data-store');

export const UPDATE_TRASH_FOLDER_DATA_STORE = createAction<{
  treeObjectIds: TreeObjectId[];
}>('trash/update-trash-folder-data-store');

export const RESTORE_SDOS_SUCCESS =
  createGraphQLSuccessAction<RestoreSdosResponse>(
    'trash/restore-sdos-successfully'
  );

export const RESTORE_SDOS_FAILURE = createGraphQLFailureAction<
  {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[]
>('trash/restore-sdos-failed');

export const actionFetchTrashFolders = (payload: {
  limit: number;
  offset: number;
}) => FETCH_TRASH_FOLDERS(payload);
export const FETCH_TRASH_FOLDERS = createAction<{
  limit: number;
  offset: number;
}>('trash/fetch-trash-folders');

export const FETCH_TRASH_FOLDERS_SUCCESS =
  createGraphQLSuccessAction<FetchSoftDeletedFoldersResponse>(
    'trash/fetch-trash-folders-successfully'
  );
export const SET_TRASH_FOLDERS_DATA = createAction<SetTrashFoldersPayload>(
  'trash/set-trash-folders-data'
);

export const FETCH_TRASH_FOLDERS_FAILURE = createGraphQLFailureAction<
  {
    readonly name: string;
    readonly data: { readonly objectId: string };
  }[]
>('trash/fetch-trash-folders-failed');

export const actionFetchCaseFilesRequest = (payload: {
  treeObjectId: TreeObjectId;
  limit: number;
  offset: number;
}) => FETCH_CASE_FILES_REQUEST(payload);
export const FETCH_CASE_FILES_REQUEST = createAction<{
  treeObjectId: TreeObjectId;
  limit: number;
  offset: number;
}>('trash/fetch-case-files-request');

export const FETCH_CASE_FILES_SUCCESS = createGraphQLSuccessAction<
  FetchCaseChildTDOsResponse,
  {
    treeObjectId: TreeObjectId;
    limit: number;
    offset: number;
  },
  true
>('trash/fetch-case-files-success');

export const FETCH_CASE_FILES_FAILURE = createGraphQLFailureAction<{
  treeObjectId: TreeObjectId;
}>('trash/fetch-case-files-failure');

export const CHECK_EXISTING_TRASH_FOLDER_INIT =
  'trash/check-existing-trash-folder-init';
export const CHECK_EXISTING_TRASH_FOLDER_SUCCESS =
  createGraphQLSuccessAction<CheckExistingTrashFolderQueryResponse>(
    'trash/check-existing-trash-folder-success'
  );
export const CHECK_EXISTING_TRASH_FOLDER_FAILURE = createGraphQLFailureAction(
  'trash/check-existing-trash-folder-failed'
);

export const UPDATE_SELECTED_FOLDER_ITEMS = createAction<{
  treeObjectId: TreeObjectId[];
}>('trash/update-selected-folder-items');

export const actionUpdateSelectedFolderItems = (payload: {
  treeObjectId: TreeObjectId[];
}) => UPDATE_SELECTED_FOLDER_ITEMS(payload);

export const DELETE_CONTENT_TEMPLATE_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteContentTemplateBulkResponse>(
    'trash/delete-content-template-bulk-successfully'
  );
export const DELETE_CONTENT_TEMPLATE_BULK_FAILURE = createGraphQLFailureAction(
  'trash/delete-content-template-bulk-failed'
);

export const DELETE_CASE_SDO_BULK_SUCCESS =
  createGraphQLSuccessAction<DeleteCaseSDOBulkResponse>(
    'trash/delete-case-sdo-bulk-successfully'
  );
export const DELETE_CASE_SDO_BULK_FAILURE = createGraphQLFailureAction(
  'trash/delete-case-sdo-bulk-failed'
);

export const UPDATE_RESTORED_FOLDER_NAME_SUCCESS =
  createGraphQLSuccessAction<RenameFolderQueryResponse>(
    'trash/update-folder-name-before-restore-successfully'
  );

export const UPDATE_RESTORED_FOLDER_NAME_FAILURE = createGraphQLFailureAction(
  'trash/update-folder-name-before-restore-failed'
);

export const SET_FILES_TABLE_PAGE_INDEX = createAction<{ pageIndex: number }>(
  'trash/set-files-table-page-index'
);
export const setFilesTablePageIndex = (pageIndex: number) =>
  SET_FILES_TABLE_PAGE_INDEX({ pageIndex });

export const SET_FILES_TABLE_PAGE_SIZE = createAction<{
  pageSize: number;
}>('trash/set-files-table-page-size');
export const setFilesTablePageSize = (pageSize: number) =>
  SET_FILES_TABLE_PAGE_SIZE({ pageSize });

export const SET_FOLDERS_TABLE_PAGE_INDEX = createAction<{ pageIndex: number }>(
  'trash/set-folders-table-page-index'
);
export const setFoldersTablePageIndex = (pageIndex: number) =>
  SET_FOLDERS_TABLE_PAGE_INDEX({ pageIndex });

export const SET_FOLDERS_TABLE_PAGE_SIZE = createAction<{
  pageSize: number;
}>('trash/set-folders-table-page-size');
export const setFoldersTablePageSize = (pageSize: number) =>
  SET_FOLDERS_TABLE_PAGE_SIZE({ pageSize });

export const CREATE_TRASH_FOLDER_INIT = createAction<void>(
  'trash/create-trash-folder-init'
);
export const CREATE_TRASH_FOLDER_SUCCESS =
  createAction<CreateNewFolderQueryResponse>(
    'trash/create-trash-folder-success'
  );
export const CREATE_TRASH_FOLDER_FAILURE = createAction(
  'trash/create-trash-folder-failure'
);

export const FETCH_SDOS_BULK_BY_TDO_IDS = createAction<{
  tdoIds: TDOId[];
}>('trash/fetch-sdos-bulk-by-tdo-ids');
export const FETCH_SDOS_BULK_BY_TDO_IDS_SUCCESS =
  createGraphQLSuccessAction<FetchSDOsBulkByTDOIdsResponse>(
    'trash/fetch-sdos-bulk-by-tdo-ids-successfully'
  );
export const FETCH_SDOS_BULK_BY_TDO_IDS_FAILURE = createGraphQLFailureAction(
  'trash/fetch-sdos-bulk-by-tdo-ids-failed'
);
export const actionFetchOrgSDOsBulkByTDOIds = (payload: { tdoIds: TDOId[] }) =>
  FETCH_SDOS_BULK_BY_TDO_IDS(payload);

export const ENSURE_FOLDER_PARENT_PATH_SUCCESS = createAction<{
  folderId: TreeObjectId;
  parentId: string;
}>('trash/ensure-folder-parent-path-success');

export const ENSURE_FOLDER_PARENT_PATH_FAILURE = createAction<{
  folderId: TreeObjectId;
}>('trash/ensure-folder-parent-path-failure');

/**
 * Purge the side data of TDOs that have just been permanently deleted
 * (VE-28551).
 *
 * The saga entry point exists because the folder/request delete happens inside
 * a SERVICE thunk — it enumerates a case's child TDOs and deletes them itself —
 * and the purge is a saga. Dispatching this is how that thunk reaches it.
 */
export const PURGE_TDO_SIDE_DATA = createAction<{ tdoIds: TDOId[] }>(
  'trash/purge-tdo-side-data'
);

export const actionPurgeTdoSideData = (payload: { tdoIds: TDOId[] }) =>
  PURGE_TDO_SIDE_DATA(payload);
